// =============================================================================
// ENHANCED SITE CONTENT DATA
// =============================================================================
// Comprehensive, engaging content that merges the best of both data sources

import type { ContentComponent, NavItem } from '@/types';

// Navigation items
export const navItems: NavItem[] = [
  { label: 'Home', href: '/' },
  { label: 'Services', href: '/services' },
  { label: 'Portfolio', href: '/portfolio' },
  { label: 'Coaching', href: '/coaching' },
  { label: 'About', href: '/about' },
  { label: 'Contact', href: '/contact' },
];

// Footer links
export const footerLinks: NavItem[] = [
  { label: 'Privacy Policy', href: '/privacy' },
  { label: 'Terms of Service', href: '/terms' },
];

export const socialLinks: NavItem[] = [
  { label: 'LinkedIn', href: 'https://www.linkedin.com/in/alex-finger-71117336a/', external: true },
  { label: 'Email', href: '/contact', external: true },
];

// Homepage content components
export const homePageComponents: ContentComponent[] = [
  {
    id: 'hero-1',
    type: 'hero',
    title: 'Maximize Your Brand\'s Digital Presence',
    content: 'Transform your business with a fully-featured marketing website that drives growth. From payment processing to appointment scheduling, get all the tools you need to reach more customers and reduce technology costs by up to 90%. Built for small businesses ready to thrive online.',
    fullWidth: true,
    metadata: {
      image: '/images/hero4.jpg',
      overlayOpacity: 0.7,
      minHeight: '70vh',
      textAlign: 'center',
      buttons: [
        {
          label: 'View Pricing',
          variant: 'primary',
          size: 'lg',
          action: 'internal',
          url: '/services'
        },
        {
          label: 'Book Free Consultation',
          variant: 'secondary',
          size: 'lg',
          action: 'internal',
          url: '/contact'
        }
      ]
    },
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    id: 'services-section',
    type: 'features',
    title: 'Everything Your Business Needs in One Package',
    content: 'Stop juggling multiple vendors and complex systems. Get a comprehensive, fully-featured website that handles everything from payments to appointments, all while reducing your technology costs and complexity.',
    metadata: {
      columns: 3,
      features: [
        {
          title: 'Complete Payment Integration',
          description: 'Accept payments directly through your website with secure Stripe integration. No third-party services needed; your customers pay you directly.',
          icon: 'fi-ts-credit-card'
        },
        {
          title: 'Appointment Scheduling',
          description: 'Let clients book appointments 24/7 with built-in scheduling functionality. Automated confirmations and calendar integration keep you organized.',
          icon: 'fi-ts-calendar'
        },
        {
          title: 'Newsletter & Marketing',
          description: 'Build your audience with integrated newsletter functionality. Grow your email list and stay connected with customers; no expensive marketing platforms required.',
          icon: 'fi-ts-envelope-bulk'
        },
        {
          title: 'SEO & Discoverability',
          description: 'Your website is fully optimized for search engines and Google indexed from day one. Show up when customers search for your services.',
          icon: 'fi-ts-rocket-hand'
        },
        {
          title: 'Simple Content Management',
          description: 'Update your content anytime with an intuitive CMS. No coding required; manage your website as easily as editing a document.',
          icon: 'fi-ts-drawer-alt'
        },
        {
          title: 'Professional Email',
          description: 'Get a custom domain email address (you@yourbusiness.com) that reinforces your brand and builds customer trust.',
          icon: 'fi-ts-at'
        }
      ]
    },
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    id: 'stats-1',
    type: 'stats',
    title: 'Real Results for Real Businesses',
    content: 'Helping small businesses establish a powerful online presence and maximize their digital capabilities.',
    metadata: {
      columns: 4,
      stats: [
        {
          number: '90%',
          label: 'Cost Reduction',
          description: 'Compared to traditional solutions'
        },
        {
          number: '$3K',
          label: 'Starting Price',
          description: 'Full-featured website'
        },
        {
          number: '100%',
          label: 'Client Satisfaction',
          description: 'Businesses we\'ve helped'
        },
        {
          number: '20+',
          label: 'Years Experience',
          description: 'Building digital solutions'
        }
      ]
    },
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    id: 'text-1',
    type: 'text',
    title: 'Reshape Your Business\'s Technology Presence',
    content: `
      <p>After two decades building complex enterprise systems, I learned that most businesses don't need complicated technology; they need powerful, accessible tools that work. Small businesses are often forced to choose between expensive, feature-rich platforms and limited, budget options. But there's a better way.</p>
      
      <p>I specialize in creating comprehensive marketing websites that give small businesses enterprise-level capabilities at a fraction of the cost. Your business gets professional payment processing, appointment scheduling, newsletter management, and more; all in one cohesive, easy-to-manage platform.</p>
      
      <h3>What Makes This Different:</h3>
      <ul>
        <li><strong>All-in-One Solution:</strong> Stop paying multiple vendors for different features. Get everything in one place.</li>
        <li><strong>True Cost Savings:</strong> Reduce technology costs by up to 90% compared to traditional solutions and SaaS subscriptions.</li>
        <li><strong>Own Your Data:</strong> Your customer data, appointments, and content belong to you; not locked in someone else's platform.</li>
        <li><strong>Simple Management:</strong> Update content, manage appointments, and process payments through an intuitive dashboard.</li>
        <li><strong>Built to Grow:</strong> Start with what you need today, easily add features as your business expands.</li>
      </ul>
      
      <p>Your business deserves a website that works as hard as you do. Let's build something that maximizes your brand exposure, streamlines your operations, and reduces your technology headaches.</p>
    `,
    metadata: {
      textAlign: 'left',
      maxWidth: 900
    },
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    id: 'cards-1',
    type: 'card',
    title: 'Choose Your Path to Success',
    content: 'Whether you need a complete website refresh or custom development, I provide solutions tailored to your business goals and growth strategy.',
    metadata: {
      columns: 3,
      cards: [
        {
          title: 'Full Branded Website',
          subtitle: 'Most Popular - $3,000',
          content: 'Transform your business with a comprehensive marketing website featuring payment processing, appointment scheduling, newsletter functionality, SEO optimization, and more. Everything you need in one place.',
          image: '/images/new-product.jpg',
          button: {
            label: 'View Details',
            variant: 'primary',
            action: 'internal',
            url: '/services'
          }
        },
        {
          title: 'Custom Development',
          subtitle: 'Build Something Unique',
          content: 'Need specialized features or a custom application? From rapid prototypes to full-scale platforms, I create tailored solutions that meet your exact requirements and budget.',
          image: '/images/architecture.jpg',
          button: {
            label: 'Explore Options',
            variant: 'secondary',
            action: 'internal',
            url: '/services'
          }
        },
        {
          title: 'Developer Coaching',
          subtitle: 'Learn to Build',
          content: 'Want to build or manage your own digital products? Get one-on-one coaching in modern development, business strategy, and creating scalable applications efficiently.',
          image: '/images/coaching-students.jpg',
          button: {
            label: 'Start Learning',
            variant: 'secondary',
            action: 'internal',
            url: '/coaching'
          }
        },
        {
          title: 'AI Code Transformation',
          subtitle: 'From Prototype to Production',
          content: 'Generated code with AI but it\'s messy or unscalable? I\'ll refactor it into clean, maintainable, production-ready code that performs and scales properly.',
          image: '/images/production-planning.jpg',
          button: {
            label: 'Transform Code',
            variant: 'secondary',
            action: 'internal',
            url: '/contact'
          }
        },
        {
          title: 'Strategic Architecture',
          subtitle: 'Data-First Design',
          content: 'Need help designing the foundation of your digital product? I\'ll create a data-driven architecture that ensures your product is built for success from day one.',
          image: '/images/requirements-gathering.jpg',
          button: {
            label: 'Plan Architecture',
            variant: 'primary',
            action: 'internal',
            url: '/contact'
          }
        },
        {
          title: 'Rapid Prototyping',
          subtitle: 'Validate Ideas Fast',
          content: 'Test your concept quickly with a working prototype. Get real user feedback before investing heavily, ensuring your final product meets actual market needs.',
          image: '/images/build-company.jpg',
          button: {
            label: 'Prototype Now',
            variant: 'secondary',
            action: 'internal',
            url: '/contact'
          }
        }
      ]
    },
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    id: 'text-2',
    type: 'text',
    title: 'How We Work Together',
    content: `
      <p>Traditional development processes are slow, expensive, and often fail to deliver what was promised. My approach is different; it's designed for speed, efficiency, and real results.</p>

      <div class="content-process">
        <h3 class="content-process__title">The 4-Week Transformation Process</h3>
        <p class="content-process__description">Most clients go from idea to market-ready product in just 4 weeks. Here's exactly how:</p>
      </div>

      <h3>Week 1: Foundation & Strategy</h3>
      <p>We start with an intensive strategy session where I help you clarify your core data model, user flows, and business objectives. By the end of this week, you'll have a complete technical blueprint that eliminates uncertainty and sets your project up for success.</p>

      <h3>Week 2: Rapid MVP Development</h3>
      <p>Using modern tools and pre-built components, I build a working prototype of your core functionality. You'll see real progress every day, and by week's end, you'll have something tangible to show stakeholders or test with users.</p>

      <h3>Week 3: Polish & Integration</h3>
      <p>We refine the user experience, integrate your branding and content, and ensure everything works seamlessly across devices. This is where your vision starts to look and feel like a professional product.</p>

      <h3>Week 4: Launch & Optimization</h3>
      <p>Your product goes live! But the work doesn't stop there; we immediately begin gathering user feedback and data to inform the next round of improvements, ensuring your product gets better over time.</p>

      <div class="content-highlight content-highlight--info">
        <h4>Ready to Start Your 4-Week Journey?</h4>
        <p>Book your free consultation today and let's discuss how we can transform your idea into a market-ready digital product in record time.</p>
        <a href="/contact" class="content-link">Book Free Consultation</a>
      </div>
    `,
    metadata: {
      textAlign: 'left',
      maxWidth: 800
    },
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    id: 'process-showcase',
    type: 'image-showcase',
    title: 'Our Proven Process in Action',
    content: 'From initial concept to market-ready solution, our streamlined approach delivers results in record time while maintaining the highest quality standards.',
    metadata: {
      backgroundImage: '/images/home-workstation.jpg',
      overlayOpacity: 0.7,
      buttons: [
        {
          label: 'View Our Portfolio',
          variant: 'primary',
          size: 'lg',
          action: 'internal',
          url: '/portfolio'
        },
        {
          label: 'Learn More About Our Process',
          variant: 'secondary',
          size: 'lg',
          action: 'internal',
          url: '/services'
        }
      ]
    },
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    id: 'success-stories',
    type: 'story-gallery',
    title: 'Success Stories & Client Results',
    content: 'Real projects, real results. See how we\'ve helped businesses transform their ideas into powerful digital solutions.',
    metadata: {
      columns: 3,
      gap: 'lg',
      galleryItems: [
        {
          id: 'yoga-encyclopedia',
          title: 'The Yoga Encyclopedia',
          description: 'Comprehensive wellness platform with continuous development and growing user engagement.',
          image: '/images/yoga-encyclopedia.jpg',
          category: 'Health & Wellness',
          tags: ['React', 'TypeScript', 'Firebase', 'Live Development'],
          button: {
            label: 'Visit Live Site',
            variant: 'primary',
            action: 'external',
            url: 'https://theyogaencyclopedia.com'
          }
        },
        {
          id: 'novel-novela',
          title: 'Novel Novela',
          description: 'Interactive storytelling platform that brings choose-your-own-adventure to the digital age.',
          image: '/images/novel-novela.jpg',
          category: 'Entertainment',
          tags: ['React', 'Interactive', 'Storytelling', 'User Experience'],
          button: {
            label: 'Visit Live Site',
            variant: 'primary',
            action: 'external',
            url: 'https://novelnovela.com'
          }
        },
        {
          id: 'rapid-prototyping',
          title: 'Rapid Prototyping Success',
          description: 'Multiple client projects delivered from concept to MVP in under 7 days, with ongoing iterative development.',
          image: '/images/laptop3.jpg',
          category: 'Business Solutions',
          tags: ['MVP', 'Rapid Development', 'Strategic Planning', 'Client Success'],
          button: {
            label: 'View Portfolio',
            variant: 'secondary',
            action: 'internal',
            url: '/portfolio'
          }
        }
      ]
    },
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    id: 'cta-1',
    type: 'hero',
    title: 'Ready to Transform Your Idea?',
    content: 'Let\'s discuss your project and explore how we can bring your vision to life with speed, precision, and strategic insight.',
    fullWidth: true,
    metadata: {
      image: '/images/handshake.jpg',
      overlayOpacity: 0.8,
      minHeight: '60vh',
      buttons: [
        {
          label: 'Schedule Consultation',
          variant: 'primary',
          size: 'lg',
          action: 'internal',
          url: '/contact'
        },
        {
          label: 'Explore Our Services',
          variant: 'secondary',
          size: 'lg',
          action: 'internal',
          url: '/services'
        }
      ]
    },
    createdAt: new Date(),
    updatedAt: new Date(),
  }
];

// Services page content
export const servicesPageComponents: ContentComponent[] = [
  {
    id: 'hero-services',
    type: 'hero',
    title: 'Comprehensive Development Services',
    content: 'From rapid prototyping to full-scale digital products, I provide end-to-end solutions that combine technical excellence with strategic business insight.',
    fullWidth: true,
    metadata: {
      image: '/images/comprehensive.jpg',
      overlayOpacity: 0.7,
      minHeight: '80vh',
      buttons: [
        {
          label: 'Get Started',
          variant: 'primary',
          size: 'lg',
          action: 'internal',
          url: '/contact'
        },
        {
          label: 'View Portfolio',
          variant: 'secondary',
          size: 'lg',
          action: 'internal',
          url: '/portfolio'
        }
      ]
    },
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    id: 'services-detailed',
    type: 'card',
    title: 'Service Offerings',
    content: 'Tailored solutions designed to meet your specific needs and objectives.',
    metadata: {
      columns: 2,
      cards: [
        {
          title: 'Web Application Development',
          subtitle: 'Custom Digital Solutions',
          content: 'Full-stack web applications built with React, TypeScript, Node.js, and modern cloud infrastructure. From simple websites to complex SaaS platforms.',
          button: {
            label: 'Learn More',
            variant: 'primary',
            action: 'internal',
            url: '/contact'
          }
        },
        {
          title: 'Business Strategy & Consulting',
          subtitle: 'Strategic Development',
          content: 'Comprehensive business development services including market analysis, product strategy, growth planning, and technical architecture decisions.',
          button: {
            label: 'Schedule Call',
            variant: 'secondary',
            action: 'internal',
            url: '/contact'
          }
        },
        {
          title: 'Rapid Prototyping',
          subtitle: 'MVP in Under a Week',
          content: 'Get your idea to market quickly with our streamlined prototyping process. Validate concepts and gather user feedback before major investment.',
          button: {
            label: 'Start Project',
            variant: 'primary',
            action: 'internal',
            url: '/contact'
          }
        },
        {
          title: 'Technical Coaching',
          subtitle: 'Learn to Build Your Own Company',
          content: 'Personalized coaching on technical skills, business development, and client acquisition. Build your own software development business.',
          button: {
            label: 'Begin Learning',
            variant: 'secondary',
            action: 'internal',
            url: '/coaching'
          }
        },
        {
          title: 'AI Code Cleanup & Refactoring',
          subtitle: 'Transform AI-Generated Code',
          content: 'Professional code cleanup and architectural refactoring for AI-generated codebases. We either clean up problematic AI code or provide the elegant solution you originally intended to build, delivered with rapid turnaround.',
          button: {
            label: 'Get Code Review',
            variant: 'primary',
            action: 'internal',
            url: '/contact'
          }
        },
        {
          title: 'Architectural Design & Data Strategy',
          subtitle: 'Information-First Product Design',
          content: 'All digital products are fundamentally about data, information, and interactions. We simplify complex solutions by focusing on data-driven architecture, then build the right technology to support optimal information delivery and user experience.',
          button: {
            label: 'Design Consultation',
            variant: 'secondary',
            action: 'internal',
            url: '/contact'
          }
        }
      ]
    },
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    id: 'service-showcase',
    type: 'image-showcase',
    title: 'Our Development Process',
    content: 'Experience the power of our streamlined approach that delivers results in record time while maintaining the highest quality standards.',
    metadata: {
      backgroundImage: '/images/collaborate.jpg',
      overlayOpacity: 0.8,
      buttons: [
        {
          label: 'Start Your Project',
          variant: 'primary',
          size: 'lg',
          action: 'internal',
          url: '/contact'
        }
      ]
    },
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    id: 'technology-gallery',
    type: 'story-gallery',
    title: 'Technologies & Expertise',
    content: 'Modern tools and proven methodologies to build scalable, maintainable, and high-performance digital solutions.',
    metadata: {
      columns: 3,
      gap: 'lg',
      galleryItems: [
        {
          id: 'frontend-tech',
          title: 'Frontend Development',
          description: 'React, TypeScript, Next.js, and modern CSS frameworks for responsive, interactive user experiences.',
          image: '/images/media-workstation.jpg',
          category: 'Frontend',
          tags: ['React', 'TypeScript', 'Next.js', 'CSS', 'Responsive Design']
        },
        {
          id: 'backend-tech',
          title: 'Backend Development',
          description: 'Node.js, Express, databases, and cloud infrastructure for robust, scalable server-side solutions.',
          image: '/images/research-workstation.jpg',
          category: 'Backend',
          tags: ['Node.js', 'Express', 'Databases', 'API Development', 'Cloud Services']
        },
        {
          id: 'business-strategy',
          title: 'Business Strategy',
          description: 'Market analysis, product strategy, and growth planning to ensure your technical solutions drive business value.',
          image: '/images/winning.jpg',
          category: 'Strategy',
          tags: ['Market Analysis', 'Product Strategy', 'Growth Planning', 'Business Development']
        }
      ]
    },
    createdAt: new Date(),
    updatedAt: new Date(),
  }
];

// Coaching page content
export const coachingPageComponents: ContentComponent[] = [
  {
    id: 'hero-coaching',
    type: 'hero',
    title: 'Build Your Own Software Development Company',
    content: 'Learn the skills, strategies, and methodologies needed to start and grow your own successful software development business. Personalized coaching from someone who\'s done it.',
    fullWidth: true,
    metadata: {
      image: '/images/cowork-women.jpg',
      overlayOpacity: 0.7,
      minHeight: '80vh',
      buttons: [
        {
          label: 'Start Your Journey',
          variant: 'primary',
          size: 'lg',
          action: 'internal',
          url: '/contact'
        },
        {
          label: 'Learn More',
          variant: 'secondary',
          size: 'lg',
          action: 'scroll',
          target: 'coaching-features'
        }
      ]
    },
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    id: 'coaching-features',
    type: 'features',
    title: 'What You\'ll Learn',
    content: 'Comprehensive training covering all aspects of building and running a successful software development business.',
    metadata: {
      columns: 3,
      features: [
        {
          title: 'Technical Mastery',
          description: 'Modern web development technologies, best practices, and scalable architecture patterns.',
          icon: 'zap'
        },
        {
          title: 'Business Development',
          description: 'Client acquisition, project management, pricing strategies, and business growth tactics.',
          icon: 'check'
        },
        {
          title: 'Strategic Thinking',
          description: 'How to analyze market needs, position your services, and build long-term client relationships.',
          icon: 'globe'
        },
        {
          title: 'Process Optimization',
          description: 'Streamlined development processes, rapid prototyping techniques, and efficient project delivery.',
          icon: 'shield'
        },
        {
          title: 'Client Management',
          description: 'Communication strategies, expectation management, and building lasting professional relationships.',
          icon: 'users'
        },
        {
          title: 'Growth Strategies',
          description: 'Scaling your business, hiring strategies, and expanding your service offerings.',
          icon: 'heart'
        }
      ]
    },
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    id: 'coaching-showcase',
    type: 'image-showcase',
    title: 'Personalized Learning Experience',
    content: 'One-on-one coaching tailored to your specific goals, learning style, and current skill level. No generic courses - just personalized guidance from someone who\'s built a successful development business.',
    metadata: {
      backgroundImage: '/images/laptop2.jpg',
      overlayOpacity: 0.8,
      buttons: [
        {
          label: 'Schedule Consultation',
          variant: 'primary',
          size: 'lg',
          action: 'internal',
          url: '/contact'
        }
      ]
    },
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    id: 'learning-path-gallery',
    type: 'story-gallery',
    title: 'Your Learning Journey',
    content: 'A structured path from beginner to business owner, with milestones and achievements that build your confidence and expertise.',
    metadata: {
      columns: 3,
      gap: 'lg',
      galleryItems: [
        {
          id: 'technical-foundation',
          title: 'Technical Foundation',
          description: 'Master modern web development technologies, best practices, and scalable architecture patterns.',
          image: '/images/home-workstation.jpeg',
          category: 'Phase 1',
          tags: ['React', 'TypeScript', 'Node.js', 'Best Practices', 'Architecture']
        },
        {
          id: 'business-skills',
          title: 'Business Development',
          description: 'Learn client acquisition, project management, pricing strategies, and business growth tactics.',
          image: '/images/handshake-group.jpg',
          category: 'Phase 2',
          tags: ['Client Acquisition', 'Project Management', 'Pricing', 'Business Strategy']
        },
        {
          id: 'company-building',
          title: 'Company Building',
          description: 'Scale your business with hiring strategies, team management, and expanding service offerings.',
          image: '/images/collab-workstation.jpg',
          category: 'Phase 3',
          tags: ['Scaling', 'Team Building', 'Operations', 'Growth Strategy']
        }
      ]
    },
    createdAt: new Date(),
    updatedAt: new Date(),
  }
];
